import { memo, useCallback, useId, useState, type FC } from "react";
import { ChevronDown, ChevronUp, ClipboardList, Forward, MonitorCheck, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { META_TEMPLATES_URL, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";

const GUIDE_KEY = `${WHATSAPP_EDITOR_KEY}.metaGuide`;

const GUIDE_STEPS = [
  { key: "create", Icon: ClipboardList },
  { key: "review", Icon: Forward },
  { key: "use", Icon: MonitorCheck },
];

interface MetaTemplateGuideProps {
  metaUrl: string | null;
}

const MetaTemplateGuide: FC<MetaTemplateGuideProps> = ({ metaUrl }) => {
  const { t } = useTranslation();
  const stepsId = useId();
  const [isOpen, setIsOpen] = useState(true);
  const toggle = useCallback(() => setIsOpen(open => !open), []);
  const ToggleIcon = isOpen ? ChevronUp : ChevronDown;

  return (
    <section className="wa-meta-guide">
      <div className="wa-meta-guide__header">
        <button
          type="button"
          className="wa-meta-guide__toggle"
          aria-expanded={isOpen}
          aria-controls={stepsId}
          onClick={toggle}
        >
          <ToggleIcon className="wa-meta-guide__chevron" aria-hidden />
          <span className="wa-meta-guide__heading">
            <span className="wa-meta-guide__title">{t(`${GUIDE_KEY}.title`)}</span>
            <span className="wa-meta-guide__subtitle">{t(`${GUIDE_KEY}.subtitle`)}</span>
          </span>
        </button>
        <a
          href={metaUrl ?? META_TEMPLATES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="button button--primary campaign-flow__action wa-meta-guide__create"
        >
          <Plus size={14} aria-hidden />
          {t(`${GUIDE_KEY}.createTemplate`)}
        </a>
      </div>

      {isOpen && (
        <ol id={stepsId} className="wa-meta-guide__steps">
          {GUIDE_STEPS.map(({ key, Icon }) => (
            <li key={key} className="wa-meta-guide__step">
              <Icon className="wa-meta-guide__step-icon" aria-hidden />
              <span className="wa-meta-guide__step-text">
                <span className="wa-meta-guide__step-title">
                  {t(`${GUIDE_KEY}.steps.${key}.title`)}
                </span>
                <span className="wa-meta-guide__step-description">
                  {t(`${GUIDE_KEY}.steps.${key}.description`)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default memo(MetaTemplateGuide);
